import React, { useState, useEffect } from 'react';
import { createAsset, uploadAssetImage } from '../../services/assetService';
import { toast } from 'react-toastify';
import styles from './AssetForm.module.css'; // Reusing styles for consistency
import { FaCamera, FaUpload, FaCheckCircle, FaSpinner } from 'react-icons/fa';

const AssetQuickSaveForm = () => {
  const [photos, setPhotos] = useState([]); // Array of { file, previewUrl }
  const [isSaving, setIsSaving] = useState(false);
  const [qrToken, setQrToken] = useState('');
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Auto-start camera on mount if qrToken is present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('qrToken');
    if (token) {
        setQrToken(token);
        // If it's a quick save from scan, start camera immediately
        startCamera("environment");
    }

    return () => {
      stopCamera();
    };
  }, []); // Run once on mount

  // CRITICAL: Attach stream only AFTER the video element is rendered
  useEffect(() => {
    if (showCamera && stream && videoRef.current) {
        console.log("Attaching stream to video element:", stream.id);
        videoRef.current.srcObject = stream;
    }
  }, [showCamera, stream]);

  const stopCamera = React.useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  }, [stream]);

  const startCamera = async (mode = facingMode) => {
    try {
      // If there's an existing stream, stop it first
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      console.log("Requesting camera stream with mode:", mode);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: mode } }
      });
      console.log("Camera stream received:", mediaStream.id);
      setStream(mediaStream);
      setShowCamera(true);
    } catch (err) {
      console.error("Error accessing camera:", err);
      toast.error("Could not access camera. Please check permissions.");
    }
  };

  const toggleCamera = () => {
    const newMode = facingMode === "environment" ? "user" : "environment";
    setFacingMode(newMode);
    startCamera(newMode);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], `capture_${Date.now()}_${photos.length}.jpg`, { type: 'image/jpeg' });
        const previewUrl = canvas.toDataURL('image/jpeg');
        setPhotos(prev => [...prev, { file, previewUrl }]);
        // Don't stop camera, allow continuous capture
        toast.info(`Photo ${photos.length + 1} captured!`, { autoClose: 1000 });
      }, 'image/jpeg', 0.8);
    }
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newPhotos = [];
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotos(prev => [...prev, { file, previewUrl: reader.result }]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleQuickSave = async () => {
    if (photos.length === 0) {
      toast.error("Please take or upload at least one photo.");
      return;
    }

    setIsSaving(true);
    try {
      // 1. Create the asset with placeholder details
      const assetName = name.trim() || `Quick Save - ${qrToken || new Date().toISOString().slice(0, 10)}`;
      const assetDescription = description.trim() || "Registered via Photo-First quick save. Details pending.";
      
      const response = await createAsset({ 
        name: assetName, 
        description: assetDescription, 
        qrToken 
      });

      console.log("Asset creation response:", response);

      if (response.success && response.data && response.data.id) {
        const assetId = response.data.id;
        
        // 2. Upload the captured photos
        const filesToUpload = photos.map(p => p.file);
        console.log("Uploading photos for asset ID:", assetId, "Count:", filesToUpload.length);
        const uploadResponse = await uploadAssetImage(assetId, filesToUpload);
        
        if (uploadResponse.success) {
          toast.success("Asset quick-saved successfully!");
          // Reset state
          setPhotos([]);
          setName('');
          setDescription('');
          stopCamera();
        } else {
          toast.warning("Asset created, but photo upload failed.");
        }
      } else {
        console.error("Failed to get asset ID from response:", response);
        toast.error("Failed to create temporary asset (ID missing).");
      }
    } catch (error) {
       console.error("Quick save error:", error);
       toast.error("An error occurred during quick save.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.container} style={{ maxWidth: '500px' }}>
      <h2 className={styles.heading}>Quick Save Asset</h2>
      <p style={{ color: '#64748b', marginBottom: '20px', textAlign: 'center' }}>
        Take a photo to register this asset immediately. You can add more details later.
      </p>

      <div className={styles.form}>
        <div style={{ 
          width: '100%', 
          aspectRatio: '1', 
          backgroundColor: '#000', 
          borderRadius: '12px', 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center',
          overflow: 'hidden',
          marginBottom: '20px',
          border: '2px solid #cbd5e1',
          position: 'relative'
        }}>
          {showCamera ? (
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              muted 
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <div style={{ textAlign: 'center', color: '#94a3b8' }}>
              <FaCamera size={48} />
              <p style={{ marginTop: '10px' }}>Camera ready</p>
            </div>
          )}
          
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>

        {/* Thumbnail Gallery */}
        {photos.length > 0 && (
          <div style={{ 
            display: 'flex', 
            gap: '10px', 
            overflowX: 'auto', 
            padding: '10px 0', 
            marginBottom: '20px',
            scrollSnapType: 'x mandatory'
          }}>
            {photos.map((p, index) => (
              <div key={index} style={{ 
                minWidth: '80px', 
                height: '80px', 
                borderRadius: '8px', 
                overflow: 'hidden', 
                position: 'relative',
                border: '2px solid #3b82f6',
                scrollSnapAlign: 'start'
              }}>
                <img src={p.previewUrl} alt={`Preview ${index}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button 
                  onClick={() => removePhoto(index)}
                  style={{ 
                    position: 'absolute', 
                    top: '2px', 
                    right: '2px', 
                    background: 'rgba(239, 68, 68, 0.9)', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: '50%', 
                    width: '20px', 
                    height: '20px', 
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  &times;
                </button>
                <div style={{
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  right: '0',
                  background: 'rgba(0,0,0,0.5)',
                  color: '#fff',
                  fontSize: '10px',
                  textAlign: 'center'
                }}>
                  #{index + 1}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '25px' }}>
            <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
              <button 
                onClick={showCamera ? capturePhoto : () => startCamera()}
                className={styles.submitButton}
                style={{ flex: 3, backgroundColor: showCamera ? '#e11d48' : '#3b82f6', border: 'none', gap: '10px' }}
              >
                <FaCamera /> {showCamera ? "Snapp Photo" : "Start Camera"}
              </button>
              
              {showCamera && (
                <button 
                  onClick={toggleCamera}
                  className={styles.submitButton}
                  style={{ flex: 1, backgroundColor: '#64748b', border: 'none' }}
                  title="Switch Camera"
                >
                  <i className="fa-solid fa-camera-rotate"></i>
                </button>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <label style={{ 
                  flex: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  gap: '8px',
                  padding: '12px',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: '#475569',
                  fontWeight: '600'
              }}>
                  <FaUpload /> Upload Images
                  <input type="file" accept="image/*" multiple onChange={handlePhotoChange} hidden />
              </label>

              {showCamera && (
                <button 
                  onClick={stopCamera}
                  style={{ flex: 1, background: '#fee2e2', border: '1px solid #fecaca', color: '#ef4444', fontWeight: '600', cursor: 'pointer', borderRadius: '8px' }}
                >
                  Close Camera
                </button>
              )}
            </div>
        </div>

        {qrToken && (
           <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe', textAlign: 'center' }}>
               <small style={{ color: '#1e40af', fontWeight: 'bold' }}>Linked Token: {qrToken}</small>
           </div>
        )}

        {/* Optional Manual Entry */}
        {photos.length > 0 && (
          <div style={{ marginBottom: '25px', display: 'flex', flexDirection: 'column', gap: '15px', animation: 'fadeIn 0.3s ease' }}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Name (Optional)</label>
              <input 
                type="text" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                placeholder="Enter asset name or leave blank to auto-gen"
                className={styles.input}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Description (Optional)</label>
              <textarea 
                value={description} 
                onChange={(e) => setDescription(e.target.value)} 
                placeholder="Enter description or leave blank"
                className={styles.textarea}
                rows={3}
              />
            </div>
          </div>
        )}

        <button 
          onClick={handleQuickSave}
          disabled={photos.length === 0 || isSaving}
          className={styles.submitButton}
          style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: '10px',
            backgroundColor: (photos.length === 0 || isSaving) ? '#cbd5e1' : '#10b981',
            cursor: (photos.length === 0 || isSaving) ? 'not-allowed' : 'pointer'
          }}
        >
          {isSaving ? <FaSpinner className="spinner" /> : <FaCheckCircle />}
          {isSaving ? "Saving..." : `Save Asset with ${photos.length} Photo${photos.length !== 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  );
};

export default AssetQuickSaveForm;
